import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

import { applicationStatusSchema } from "../../application";
import { extensionInstallIdSchema } from "./claim-request";

export const resultOutcomeSchema = z.enum([
  "applied",
  "already_applied",
  "needs_attention",
  "failed",
  "captcha",
]);

export type ResultOutcome = z.infer<typeof resultOutcomeSchema>;

/**
 * Plan 4.1 — one step of the extension's Easy Apply modal walk. Mirrors
 * `StepTraceEntry` in `apps/extension/src/content/shared/board-runner.ts`;
 * kept as an independent schema here (rather than importing the extension's
 * type) because this package cannot depend on the extension app, and because
 * the wire contract needs its own size limits regardless of what the client
 * happens to send.
 */
/**
 * Plan F4 — "raw button text leaves the browser". Mirrors the extension's
 * own `ACTION_WORD_ALLOWLIST` in
 * `apps/extension/src/content/linkedin/action-label.ts` — kept as an
 * independent list here (not imported) for the same reason this file
 * doesn't import `StepTraceEntry` itself: this package cannot depend on the
 * extension app. That independence is the point: an out-of-date extension
 * build that skips `normalizeActionLabel` and sends a raw label straight
 * through is still rejected here, server-side, regardless of what the
 * client itself did or didn't scrub. Keep both lists in sync by hand if this
 * one changes.
 */
const ACTION_WORD_ALLOWLIST = new Set([
  "next",
  "continue",
  "review",
  "submit",
  "apply",
  "done",
  "back",
  "close",
]);

/** Matches `normalizeActionLabel`'s own `[unlisted:<length>:<8-char hash>]` shape. */
const UNLISTED_BUTTON_LABEL_PATTERN = /^\[unlisted:\d+:[\da-f]{8}]$/;

function isAllowedButtonLabel(value: string | null): boolean {
  if (value === null) return true;
  return ACTION_WORD_ALLOWLIST.has(value) || UNLISTED_BUTTON_LABEL_PATTERN.test(value);
}

export const stepTraceEntrySchema = z
  .object({
    step: z.number().int().min(1),
    buttonAriaLabel: z.string().max(300).nullable(),
    buttonText: z.string().max(300).nullable(),
    action: z.enum([
      "clicked_next",
      "clicked_submit",
      "non_advancing",
      "no_button_found",
      "captcha_detected",
      "missing_answer",
      "dialog_not_found",
    ]),
    // A hash, not the dialog's raw text — see StepTraceEntry's own doc comment
    // for why the raw text (which routinely contains what the user typed into
    // screening questions) never leaves the browser.
    dialogFingerprint: z.string().max(32),
  })
  .superRefine((val, ctx) => {
    if (!isAllowedButtonLabel(val.buttonAriaLabel)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["buttonAriaLabel"],
        message:
          "buttonAriaLabel must be an allow-listed action word (next/continue/review/submit/apply/done/back/close) or an [unlisted:...] marker",
      });
    }
    if (!isAllowedButtonLabel(val.buttonText)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["buttonText"],
        message:
          "buttonText must be an allow-listed action word (next/continue/review/submit/apply/done/back/close) or an [unlisted:...] marker",
      });
    }
  });

export type StepTraceEntryDto = z.infer<typeof stepTraceEntrySchema>;

/**
 * `easy-apply-runner.ts`'s MAX_STEPS is 10, and the runner pushes at most one
 * entry per loop iteration (see its comment on `trace`) — so a well-behaved
 * client never sends more than 10. This cap is a server-side backstop, not a
 * mirror of that constant: it keeps the persisted `Application.extensionStepTrace`
 * Json column bounded even if a future client build (or a compromised one)
 * sends more, independent of whatever the extension's own loop bound is.
 */
const MAX_TRACE_STEPS = 12;

export const resultReportRequestSchema = z
  .object({
    applicationId: z.string().min(1),
    installId: extensionInstallIdSchema,
    outcome: resultOutcomeSchema,
    reason: z.string().max(500).optional(),
    screenshotPresignedKey: z.string().optional(),
    trace: z.array(stepTraceEntrySchema).max(MAX_TRACE_STEPS).optional(),
  })
  .superRefine((val, ctx) => {
    const needsReason: ResultOutcome[] = ["needs_attention", "failed", "captcha"];
    if (needsReason.includes(val.outcome) && !val.reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["reason"],
        message: `reason is required when outcome is '${val.outcome}'`,
      });
    }
  });

export class ResultReportRequestDto extends createZodDto(resultReportRequestSchema) {}

export const resultReportResponseSchema = z.object({
  /**
   * The status the row ACTUALLY holds after the report — not one of the three
   * outcomes the extension can produce.
   *
   * A result report that arrives on a row the extension does not own (the user
   * already closed it as EXPIRED, WITHDRAWN or ACCEPTED, or a stale service
   * worker flushed its retry queue days later) is answered with the real status
   * and writes nothing. Narrowing this back to the three submission outcomes
   * would force the server to either lie about what the row says or overwrite
   * the user's answer, which is the bug this widening exists to prevent.
   */
  status: applicationStatusSchema,
  appliedAt: z.string().datetime().optional(),
  errorMessage: z.string().optional(),
});

export type ResultReportResponseDto = z.infer<typeof resultReportResponseSchema>;
